import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RegionMaster, CountryMaster } from '../../types/master';

interface Props {
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const RegionMasterTab: React.FC<Props> = ({ onError, onSuccess }) => {
  const [regions, setRegions] = useState<RegionMaster[]>([]);
  const [countries, setCountries] = useState<CountryMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState<RegionMaster | null>(null);
  const [filterCountry, setFilterCountry] = useState<string>('');
  const [formData, setFormData] = useState({
    countryCode: '',
    code: '',
    nameJa: '',
    nameTh: '',
    nameEn: '',
    sortOrder: 0,
    isActive: true,
  });

  const functions = getFunctions();

  useEffect(() => {
    fetchCountries();
    fetchRegions();
  }, []);

  useEffect(() => {
    if (filterCountry) {
      fetchRegions();
    }
  }, [filterCountry]);

  const fetchCountries = async () => {
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const result = await getMasterData({ type: 'countries' });
      const data = (result.data as any).data as CountryMaster[];
      setCountries(data);
    } catch (error: any) {
      onError(error.message || '国マスタの取得に失敗しました');
    }
  };

  const fetchRegions = async () => {
    setLoading(true);
    try {
      const getMasterData = httpsCallable(functions, 'getMasterData');
      const params: any = { type: 'regions', includeInactive: true };
      if (filterCountry) {
        params.countryCode = filterCountry;
      }
      const result = await getMasterData(params);
      const data = (result.data as any).data as RegionMaster[];
      setRegions(data);
    } catch (error: any) {
      onError(error.message || '地方マスタの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (region?: RegionMaster) => {
    if (region) {
      setEditingRegion(region);
      setFormData({
        countryCode: region.countryCode,
        code: region.code,
        nameJa: region.name.ja,
        nameTh: region.name.th || '',
        nameEn: region.name.en || '',
        sortOrder: region.sortOrder,
        isActive: region.isActive,
      });
    } else {
      setEditingRegion(null);
      setFormData({
        countryCode: filterCountry || countries[0]?.code || '',
        code: '',
        nameJa: '',
        nameTh: '',
        nameEn: '',
        sortOrder: regions.length + 1,
        isActive: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRegion(null);
  };

  const handleSave = async () => {
    try {
      if (!formData.countryCode || !formData.code || !formData.nameJa) {
        onError('必須項目を入力してください');
        return;
      }

      if (editingRegion) {
        const updateMasterData = httpsCallable(functions, 'updateMasterData');
        await updateMasterData({
          id: editingRegion.id,
          type: 'regions',
          data: {
            countryCode: formData.countryCode,
            code: formData.code,
            name: {
              ja: formData.nameJa,
              th: formData.nameTh || undefined,
              en: formData.nameEn || undefined,
            },
            sortOrder: formData.sortOrder,
            isActive: formData.isActive,
          },
        });
        onSuccess('地方マスタを更新しました');
      } else {
        const createRegion = httpsCallable(functions, 'createRegion');
        await createRegion({
          countryCode: formData.countryCode,
          code: formData.code,
          name: {
            ja: formData.nameJa,
            th: formData.nameTh || undefined,
            en: formData.nameEn || undefined,
          },
          sortOrder: formData.sortOrder,
          isActive: formData.isActive,
        });
        onSuccess('地方マスタを作成しました');
      }

      handleCloseDialog();
      fetchRegions();
    } catch (error: any) {
      onError(error.message || '保存に失敗しました');
    }
  };

  const handleDelete = async (region: RegionMaster) => {
    if (!window.confirm(`${region.name.ja}を削除してもよろしいですか？`)) {
      return;
    }

    try {
      const deleteMasterData = httpsCallable(functions, 'deleteMasterData');
      const result = await deleteMasterData({
        id: region.id,
        type: 'regions',
      });
      onSuccess((result.data as any).message || '地方マスタを削除しました');
      fetchRegions();
    } catch (error: any) {
      onError(error.message || '削除に失敗しました');
    }
  };

  const getCountryName = (countryCode: string) => {
    return countries.find(c => c.code === countryCode)?.name.ja || countryCode;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            地方を追加
          </Button>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>国でフィルタ</InputLabel>
            <Select
              value={filterCountry}
              label="国でフィルタ"
              onChange={(e) => setFilterCountry(e.target.value)}
            >
              <MenuItem value="">全て</MenuItem>
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>国</TableCell>
              <TableCell>コード</TableCell>
              <TableCell>名称（日本語）</TableCell>
              <TableCell>名称（タイ語）</TableCell>
              <TableCell>名称（英語）</TableCell>
              <TableCell>表示順</TableCell>
              <TableCell>状態</TableCell>
              <TableCell align="right">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {regions.map((region) => (
              <TableRow key={region.id}>
                <TableCell>{getCountryName(region.countryCode)}</TableCell>
                <TableCell>{region.code}</TableCell>
                <TableCell>{region.name.ja}</TableCell>
                <TableCell>{region.name.th || '-'}</TableCell>
                <TableCell>{region.name.en || '-'}</TableCell>
                <TableCell>{region.sortOrder}</TableCell>
                <TableCell>
                  <Chip
                    label={region.isActive ? '有効' : '無効'}
                    color={region.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(region)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(region)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRegion ? '地方マスタを編集' : '地方マスタを追加'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>国</InputLabel>
            <Select
              value={formData.countryCode}
              label="国"
              onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
              disabled={!!editingRegion}
            >
              {countries.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  {country.name.ja}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="コード"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
            margin="normal"
            required
            disabled={!!editingRegion}
          />
          <TextField
            fullWidth
            label="名称（日本語）"
            value={formData.nameJa}
            onChange={(e) => setFormData({ ...formData, nameJa: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="名称（タイ語）"
            value={formData.nameTh}
            onChange={(e) => setFormData({ ...formData, nameTh: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称（英語）"
            value={formData.nameEn}
            onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="表示順"
            type="number"
            value={formData.sortOrder}
            onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
            margin="normal"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="有効"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleSave} variant="contained">
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RegionMasterTab;
